import React, { useEffect, useRef, useState } from "react";

import { useStore } from "../store";
import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { sendChat, useChatSubscription } from "./wire";

/**
 * Lobby chat panel. Displays the chat log from the zustand store and an
 * input row for sending new messages. Sending and subscription are owned
 * by Lane E (./wire).
 */
export function ChatPanel(): JSX.Element {
  const log = useStore((s) => s.chatLog);
  // Chat write is wallet-gated only once the token economy is live (server
  // sets `chatGated`). Then a verified pubkey (from the on-join attestation) is
  // required; pre-launch chat stays open to everyone.
  const canChat = useStore((s) => !s.chatGated || !!s.localPlayer?.pubkey);
  const isTouch = useIsTouch();
  const [text, setText] = useState("");
  // Collapse by default on touch so the panel doesn't cover the EcctrlJoystick
  // at bottom-left until the player taps the header to expand it.
  const [collapsed, setCollapsed] = useState(isTouch);
  const logRef = useRef<HTMLDivElement>(null);

  // Lane E hook — wire it up when present. The cast is to a callable so the
  // ts-expect-error above stays narrowly scoped.
  const useSub = useChatSubscription as undefined | (() => void);
  if (typeof useSub === "function") useSub();

  // Auto-scroll log on new entries.
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log.length]);

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    try {
      const send = sendChat as undefined | ((body: string) => void);
      if (typeof send === "function") send(body);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[chat] sendChat failed:", err);
    }
    setText("");
  };

  // On touch we dock bottom-right (joystick owns bottom-left); on desktop
  // we keep the original bottom-left placement.
  const anchor = isTouch
    ? { right: 12, bottom: 12, safeAreaBottom: true }
    : { left: 12, bottom: 12 };

  return (
    <DraggableOverlay
      id="chat-panel"
      defaultAnchor={anchor}
      enabled={isTouch}
      zIndex={10}
      // Inputs need normal pointer behavior; the scrollable log needs to
      // scroll inside the panel rather than dragging the whole thing.
      nonDragSelector="input, textarea, button, [data-chat-log]"
    >
      <div
        style={{
          width: isTouch ? 220 : 320,
          maxHeight: collapsed ? 36 : isTouch ? 200 : 260,
          background: "rgba(15,16,24,0.92)",
          border: "1px solid #2a2a36",
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontSize: 13,
          color: "#e8e8ef",
        }}
      >
        <div
          onClick={() => setCollapsed((c) => !c)}
          style={{
            padding: "6px 10px",
            background: "#191b27",
            borderBottom: collapsed ? "none" : "1px solid #2a2a36",
            cursor: isTouch ? "move" : "pointer",
            userSelect: "none",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>Lobby Chat</span>
          <span style={{ color: "#9aa0b4" }}>{collapsed ? "▴" : "▾"}</span>
        </div>
        {!collapsed && (
          <>
            <div
              ref={logRef}
              data-chat-log
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "6px 10px",
                minHeight: 140,
                // Re-enable native scroll inside the panel — wrapper sets
                // touch-action:none to take over pan gestures.
                touchAction: "pan-y",
              }}
            >
              {log.length === 0 ? (
                <div style={{ color: "#6c7088", fontStyle: "italic" }}>
                  No messages yet.
                </div>
              ) : (
                log.map((m) => (
                  <div key={m.id} style={{ marginBottom: 4 }}>
                    <span
                      style={{ color: m.holder ? "#f4d36a" : "#8bb6ff", fontWeight: 600 }}
                      title={m.holder ? "token holder" : undefined}
                    >
                      {m.holder ? "✦ " : ""}
                      {m.from}:
                    </span>{" "}
                    <span>{m.body}</span>
                  </div>
                ))
              )}
            </div>
            {canChat ? (
              <form
                onSubmit={onSubmit}
                style={{
                  display: "flex",
                  borderTop: "1px solid #2a2a36",
                  padding: 6,
                  gap: 6,
                }}
              >
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Say something…"
                  maxLength={280}
                  style={{
                    flex: 1,
                    background: "#0e0f17",
                    border: "1px solid #2a2a36",
                    borderRadius: 4,
                    padding: "4px 8px",
                    color: "#e8e8ef",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
                <button
                  type="submit"
                  style={{
                    background: "#3b6ef5",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Send
                </button>
              </form>
            ) : (
              <div
                style={{
                  borderTop: "1px solid #2a2a36",
                  padding: "8px 10px",
                  fontSize: 12,
                  color: "#9aa0b4",
                  fontStyle: "italic",
                }}
              >
                Connect a wallet to chat.
              </div>
            )}
          </>
        )}
      </div>
    </DraggableOverlay>
  );
}
